import { useMutation, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next"
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Table } from "reactstrap";
import SubscriptionService from "src/apis/SubscriptionService";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import Navigator from "src/components/Navigator";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_TIME_FORMAT,
    OrdersStatuses,
    SubscriptionsStatues
} from "src/common/constants";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { RiCloseCircleLine } from "react-icons/ri";
import RequestCancellationModal from "../components/RequestCancellationModal";
import { useState } from "react";

const formatPrice = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
    }).format(Number(value || 0));
};

const formatDate = (date) => {
    if (!date) return '-';

    const dateUtils = new DateUtils();

    return dateUtils.convertDateToDate(
        date,
        INTERNATIONAL_DATE_TIME_FORMAT
    );
};

const InfoItem = ({ label, value }) => {
    return (
        <div>
            <div className="text-muted mb-1">
                {label}
            </div>

            <div className="fw-semibold">
                {value || '-'}
            </div>
        </div>
    );
};

const SummaryItem = ({ label, value, strong = false }) => {
    return (
        <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">
                {label}
            </span>

            <span className={strong ? 'fw-bold fs-5' : 'fw-semibold'}>
                {value}
            </span>
        </div>
    );
};

const AddressBlock = ({ address }) => {
    return (
        <div className="d-flex flex-column gap-2">
            <div className="fw-semibold">
                {address?.first_name} {address?.last_name}
            </div>

            {address?.company && (
                <div>{address.company}</div>
            )}

            <div>{address?.address_1}</div>

            {address?.address_2 && (
                <div>{address.address_2}</div>
            )}

            <div>
                {address?.city}, {address?.postcode}
            </div>

            <div>{address?.country}</div>

            {address?.phone && (
                <div>{address.phone}</div>
            )}
        </div>
    );
};

const SubscriptionDetailBody = ({ orderId, subscriptionId, t }) => {
    const [ openCancellModal, setOpenCancellModal ] = useState(false);

    const fetchSubscriptionDetailQuery = useQuery({
        queryKey: [
            "app-fetch-order-subscription-detail-query",
            "wb",
            orderId,
            subscriptionId
        ],
        queryFn: async () => {
            const service = SubscriptionService.getInstance();

            return await service.fetchSubscriptionDetail(subscriptionId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: true,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('Failed to load subscription details.'), {
                type: "error"
            });
        }
    });

    const submitCancelRequestMutation = useMutation({
        mutationFn: async () => {
            const service = SubscriptionService.getInstance();

            return await service.sendCancelSubscriptionRequest(subscriptionId);
        },
        onError: () => {
            toast(t('Failed to submit cancellation request.'), {
                type: 'error'
            })
        },
        onSuccess: () => {
            toast(t('Cancellation request submitted successfully.'), {
                type: 'success'
            });

            setOpenCancellModal(false);

            fetchSubscriptionDetailQuery.refetch();
        }
    });

    if (fetchSubscriptionDetailQuery.isFetching) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
            </div>
        );
    }

    const toggleCancellModal = () => setOpenCancellModal(!openCancellModal);

    const {
        subscription = {}
    } = fetchSubscriptionDetailQuery.data || {};

    const status = SubscriptionsStatues[subscription?.status];

    return (
        <div>
            {/* PAGE HEADER */}
            <Row className="align-items-center mb-4">
                <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0">
                            {t('Subscription')} #{subscription?.id}
                        </h4>

                        <Badge
                            style={{
                                backgroundColor: status ? status.color : "#E0E0E0"
                            }}
                            pill
                        >
                            {t(status ? status.title : subscription?.status)}
                        </Badge>
                    </div>
                </Col>

                <Col md={4}>
                    <div className="d-flex justify-content-md-end gap-2 mt-3 mt-md-0">
                        {status?.isCancellable && (
                            <Button
                                color="danger"
                                onClick={() => setOpenCancellModal(true)}
                            >
                                <RiCloseCircleLine size="18" className="me-1" />
                                {t('Cancel Subscription')}
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            <Row>
                {/* LEFT SIDE */}
                <Col lg={8}>
                    {/* SUBSCRIPTION ITEMS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Subscription Items')}
                            </h5>
                        </CardHeader>

                        <CardBody className="pt-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>
                                                {t('Product')}
                                            </th>
                                            <th>{t('Quantity')}</th>
                                            <th>{t('Unit Price')}</th>
                                            <th>{t('Discount')}</th>
                                            <th>{t('VAT')}</th>
                                            <th className="text-end">
                                                {t('Total')}
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {subscription?.line_items?.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        {item.image && (
                                                            <img
                                                                src={item.image}
                                                                alt={item.name}
                                                                className="rounded border"
                                                                width={48}
                                                                height={48}
                                                            />
                                                        )}

                                                        <div>
                                                            <h6 className="mb-1">
                                                                {item.name}
                                                            </h6>

                                                            <small className="text-muted">
                                                                {t('Product ID')}: {item.product_id}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td>
                                                    {item.quantity}
                                                </td>

                                                <td>
                                                    {formatPrice(
                                                        item.subtotal,
                                                        subscription.currency
                                                    )}
                                                </td>

                                                <td>
                                                    {formatPrice(
                                                        item.subtotal - item.total,
                                                        subscription.currency
                                                    )}
                                                </td>

                                                <td>
                                                    {formatPrice(
                                                        item.total_tax,
                                                        subscription.currency
                                                    )}
                                                </td>

                                                <td className="text-end fw-bold">
                                                    {formatPrice(
                                                        item.total + item.total_tax,
                                                        subscription.currency
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>

                    {/* RELATED ORDERS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Related Orders')}
                            </h5>
                        </CardHeader>

                        <CardBody className="pt-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t('Order ID')}</th>
                                            <th>{t('Relationship')}</th>
                                            <th>{t('Status')}</th>
                                            <th>{t('Created At')}</th>
                                            <th className="text-end">
                                                {t('Total')}
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {subscription?.related_orders?.map((order) => {
                                            const orderStatus = OrdersStatuses[order.status];

                                            return (
                                                <tr key={order.id}>
                                                    <td className="fw-semibold">
                                                        <Link
                                                            to={`/admin/administration/orders/${order.id}`}
                                                        >
                                                            #{order.number}
                                                        </Link>
                                                    </td>

                                                    <td className="text-capitalize">
                                                        {t(order.relationship)}
                                                    </td>

                                                    <td>
                                                        <Badge
                                                            style={{
                                                                backgroundColor: orderStatus ? orderStatus.color : "#E0E0E0"
                                                            }}
                                                        >
                                                            {t(orderStatus ? orderStatus.title : subscription?.status)}
                                                        </Badge>
                                                    </td>

                                                    <td>
                                                        {formatDate(order.date_created)}
                                                    </td>

                                                    <td className="text-end fw-semibold">
                                                        {formatPrice(
                                                            order.total,
                                                            subscription.currency
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>

                    {/* ACTIVITY */}
                    <Card>
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Subscription Notes')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-4">
                                {subscription?.notes?.filter((note) => note.customer_note)?.map((note) => (
                                    <div
                                        key={note.id}
                                        className="border-bottom pb-3"
                                    >
                                        <div className="d-flex justify-content-between mb-2">
                                            <div className="fw-semibold">
                                                {note.added_by}
                                            </div>

                                            <small className="text-muted">
                                                {formatDate(note.date_created)}
                                            </small>
                                        </div>

                                        <div className="text-muted">
                                            {note.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* RIGHT SIDE */}
                <Col lg={4}>
                    {/* GENERAL INFORMATION */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('General Information')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">
                                <InfoItem
                                    label={t('Subscription ID')}
                                    value={`#${subscription?.id}`}
                                />

                                <InfoItem
                                    label="Status"
                                    value={
                                        <Badge
                                            style={{
                                                backgroundColor: status ? status.color : "#E0E0E0"
                                            }}
                                        >
                                            {t(status ? status.title : subscription?.status)}
                                        </Badge>
                                    }
                                />

                                <InfoItem
                                    label={t('Customer')}
                                    value={subscription?.formatted_billing_full_name}
                                />

                                <InfoItem
                                    label={t('Company')}
                                    value={subscription?.billing_company}
                                />

                                <InfoItem
                                    label={t('Created At')}
                                    value={formatDate(subscription?.date_created)}
                                />

                                <InfoItem
                                    label={t('Paid At')}
                                    value={formatDate(subscription?.date_paid)}
                                />

                                <InfoItem
                                    label={t('Parent Order')}
                                    value={`#${subscription?.parent_id}`}
                                />

                                <InfoItem
                                    label={t('Currency')}
                                    value={subscription?.currency}
                                />

                                <InfoItem
                                    label={t('Renewal Type')}
                                    value={subscription?.payment_method_to_display}
                                />
                            </div>
                        </CardBody>
                    </Card>

                    {/* RENEWAL INFORMATION */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Renewal Information')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">
                                <InfoItem
                                    label={t('Billing Cycle')}
                                    value={`Every ${subscription?.billing_interval} ${subscription?.billing_period}`}
                                />

                                <InfoItem
                                    label={t('Start Date')}
                                    value={formatDate(subscription?.dates?.start)}
                                />

                                <InfoItem
                                    label={t('Next Renewal')}
                                    value={formatDate(subscription?.dates?.next_payment)}
                                />

                                <InfoItem
                                    label={t('Manual Renewal')}
                                    value={
                                        subscription?.requires_manual_renewal
                                            ? t('Yes')
                                            : t('No')
                                    }
                                />

                                <InfoItem
                                    label={t('Payment Count')}
                                    value={subscription?.payment_count}
                                />

                                <InfoItem
                                    label={t('Failed Payments')}
                                    value={subscription?.failed_payment_count}
                                />
                            </div>
                        </CardBody>
                    </Card>

                    {/* BILLING DETAILS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Billing Details')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">
                                <InfoItem
                                    label={t('Email')}
                                    value={subscription?.billing_email}
                                />

                                <InfoItem
                                    label={t('Phone')}
                                    value={subscription?.billing_phone}
                                />

                                <InfoItem
                                    label={t('VAT Number')}
                                    value={
                                        subscription?.meta_data?.find(
                                            (item) =>
                                                item.key === '_billing_eu_vat_number'
                                        )?.value
                                    }
                                />

                                <InfoItem
                                    label={t('Payment Method')}
                                    value={
                                        subscription?.payment_method_to_display || '-'
                                    }
                                />
                            </div>
                        </CardBody>
                    </Card>

                    {/* CUSTOMER ADDRESS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Customer Address')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <AddressBlock
                                address={subscription?.shipping_address}
                            />
                        </CardBody>
                    </Card>

                    {/* FINANCIAL SUMMARY */}
                    <Card>
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Financial Summary')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">
                                <SummaryItem
                                    label={t('Subtotal')}
                                    value={formatPrice(
                                        subscription?.subtotal,
                                        subscription?.currency
                                    )}
                                />

                                <SummaryItem
                                    label={t('Discount')}
                                    value={formatPrice(
                                        subscription?.discount_total,
                                        subscription?.currency
                                    )}
                                />

                                <SummaryItem
                                    label={t('VAT')}
                                    value={formatPrice(
                                        subscription?.total_tax,
                                        subscription?.currency
                                    )}
                                />

                                <hr className="my-1" />

                                <SummaryItem
                                    label={t('Recurring Total')}
                                    value={`${formatPrice(
                                        subscription?.total,
                                        subscription?.currency
                                    )} / ${t(subscription?.billing_period)}`}
                                    strong
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <RequestCancellationModal
                isOpen={openCancellModal}
                toggle={toggleCancellModal}
                subscription={subscription}
                onConfirm={submitCancelRequestMutation.mutate}
                isLoading={submitCancelRequestMutation.isLoading}
            />
        </div>
    );
};

const SubscriptionDetail = ({ t }) => {
    const params = useParams();

    if (!isFeatureEnabledForActiveModule('subscription_report')) {
        return (
            <div className="page-content">
                <Alert
                    color="warning"
                >
                    <p className="mb-0">
                        {t("Please upgrade your plan to access this feature")}.
                    </p>
                </Alert>
            </div>
        )
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle="Subscription Detail"
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                { title: "Orders", link: "/admin/administration/orders" },
                                { title: `#${params?.id}`, link: `/admin/administration/orders/${params?.id}` },
                                { title: "Subscription Detail", link: "#" },
                            ]}
                        />
                    </Col>
                </Row>

                <SubscriptionDetailBody
                    orderId={params?.id}
                    subscriptionId={params?.subscriptionId}
                    t={t}
                />
            </Container>
        </div>
    )
}

export default withNamespaces()(
    SubscriptionDetail
);